// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Gauge, ShieldCheck, HeartPulse, AlertTriangle, Users, DollarSign } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState } from '../../components/workflow/StatePills'
import { BarChart, DonutChart } from '../../components/charts/Charts'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: a funnel on every column; each value is what the cell shows.
const QUEUE_FUNNELS: FunnelColumn<api.StaffProductivity['queue_depth'][number]>[] = [
  { key: 'state', label: 'Queue (state)', value: q => q.state },
  { key: 'count', label: 'Waiting', value: q => q.count },
]

const TEAL = '#0f9488', BLUE = '#4a6fa5', GOLD = '#d9a02b', RED = '#b4472e', MUTE = '#69788c'

const EMPTY_TAT: api.SpeedToTherapy = { completed_count: 0, median_days: 0, p90_days: 0, same_week_pct: 0, distribution: [], scoped: false }
const EMPTY_PA: api.PriorAuthDash = { approved: 0, denied: 0, pending: 0, approval_pct: 0, denial_pct: 0, by_state: [], scoped: false }
const EMPTY_ADH: api.AdherenceDash = { total: 0, covered: 0, overdue: 0, due_soon: 0, coverage_pct: 0, scoped: false }
const EMPTY_EXC: api.ExceptionsDash = { open_total: 0, aging_over_3d: 0, by_reason: [], scoped: false }
const EMPTY_STAFF: api.StaffProductivity = { team_members: 0, rx_touches: 0, per_user: [], queue_depth: [], scoped: false }
const EMPTY_FIN: api.FinancialSummary = { rx_count: 0, revenue: 0, cost: 0, profit: 0, margin_pct: 0, copay_captured: 0, top_drugs: [], scoped: false }
const usd = (n: number) => {
  const neg = n < 0, a = Math.abs(n)
  const s = a >= 1000
    ? `$${(a / 1000).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`
    : `$${a.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
  return neg ? `-${s}` : s
}

type Bundle = {
  tat: api.SpeedToTherapy
  pa: api.PriorAuthDash
  adh: api.AdherenceDash
  exc: api.ExceptionsDash
  staff: api.StaffProductivity
  fin: api.FinancialSummary
}

type Period = 'month' | 'quarter' | 'lastq' | 'year' | 'lasty' | 'all'
const PERIODS: { key: Period; label: string }[] = [
  { key: 'month', label: 'This month' },
  { key: 'quarter', label: 'This quarter' },
  { key: 'lastq', label: 'Last quarter' },
  { key: 'year', label: 'This year' },
  { key: 'lasty', label: 'Last year' },
  { key: 'all', label: 'All time' },
]
function rangeFor(p: Period): api.DashRange {
  const now = new Date(), y = now.getFullYear(), m = now.getMonth(), q = Math.floor(m / 3)
  const iso = (d: Date) => d.toISOString()
  switch (p) {
    case 'month': return { from: iso(new Date(y, m, 1)) }
    case 'quarter': return { from: iso(new Date(y, q * 3, 1)) }
    case 'lastq': return { from: iso(new Date(y, q * 3 - 3, 1)), to: iso(new Date(y, q * 3, 1)) }
    case 'year': return { from: iso(new Date(y, 0, 1)) }
    case 'lasty': return { from: iso(new Date(y - 1, 0, 1)), to: iso(new Date(y, 0, 1)) }
    default: return {}
  }
}

export default function InsightsDashboard() {
  const [data, setData] = useState<Bundle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [degraded, setDegraded] = useState<string[]>([])
  const [period, setPeriod] = useState<Period>('all')

  useEffect(() => {
    setLoading(true); setError(null)
    const r = rangeFor(period)
    Promise.allSettled([
      api.getSpeedToTherapy(r),
      api.getPriorAuthDash(r),
      api.getAdherenceDash(r),
      api.getExceptionsDash(r),
      api.getStaffProductivity(r),
      api.getFinancialSummary(r),
    ]).then(([tatR, paR, adhR, excR, staffR, finR]) => {
      const failed: string[] = []
      function pick<T>(r: PromiseSettledResult<T>, fb: T, name: string): T {
        if (r.status === 'fulfilled') return r.value
        failed.push(name); return fb
      }
      setDegraded(failed)
      setData({
        tat: pick(tatR, EMPTY_TAT, 'Speed-to-Therapy'),
        pa: pick(paR, EMPTY_PA, 'Prior-Auth'),
        adh: pick(adhR, EMPTY_ADH, 'Adherence'),
        exc: pick(excR, EMPTY_EXC, 'Exceptions'),
        staff: pick(staffR, EMPTY_STAFF, 'Staff Productivity'),
        fin: pick(finR, EMPTY_FIN, 'Financial'),
      })
    }).catch(e => setError(friendlyError(e))).finally(() => setLoading(false))
  }, [period])

  // D-UI-2: hook stays above the early returns; the rows are empty until the data arrives.
  const queueFx = useTableFunnels(data?.staff.queue_depth, QUEUE_FUNNELS)

  if (loading) return <div style={pageStyle}><LoadingState label="Loading analytics…" /></div>
  if (error)   return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!data)   return null

  const { tat, pa, adh, exc, staff, fin } = data

  return (
    <div style={pageStyle}>
      <style>{`
        .ins-2col{ display:grid; grid-template-columns:minmax(0,1fr) auto; gap:16px; align-items:center; }
        .ins-2col > *{ min-width:0; }
        @media (max-width:640px){
          .ins-2col{ grid-template-columns:1fr; }
          .ins-2col svg{ max-width:160px; margin:0 auto; }
        }
      `}</style>
      <header style={{ marginBottom: 24 }}>
        <h1 style={titleStyle}>Analytics</h1>
        <p style={subtitleStyle}>Speed-to-therapy, payer access, adherence, exceptions and staff workload — from this pharmacy's own data.</p>
        <div style={selectorRow}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{ ...periodBtn, ...(period === p.key ? periodBtnActive : {}) }}
            >{p.label}</button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--color-text-mute, #69788c)', marginLeft: 4 }}>by Rx creation date</span>
        </div>
      </header>

      {degraded.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBanner message={`Some tiles couldn't load right now (${degraded.join(', ')}). Showing everything else.`} />
        </div>
      )}

      {/* Financial */}
      <Section icon={DollarSign} title="Financial Performance">
        <div style={statRow}>
          <Stat k="Net revenue" v={usd(fin.revenue)} tone={TEAL} d={`${fin.rx_count} adjudicated Rx`} />
          <Stat k="Drug + fee cost" v={usd(fin.cost)} d="acquisition + dispensing" />
          <Stat k="Gross profit" v={usd(fin.profit)} tone={fin.profit >= 0 ? TEAL : RED} d={`${fin.margin_pct}% margin`} />
          <Stat k="Copay captured" v={usd(fin.copay_captured)} tone={GOLD} d="patient responsibility" />
        </div>
        {fin.top_drugs.length > 0
          ? <BarChart data={fin.top_drugs.map(d => ({ label: d.drug, value: Math.round(d.revenue) }))} color={TEAL} />
          : <p style={noteStyle}>No adjudicated prescriptions yet — rows without a paid claim (revenue) are excluded.</p>}
      </Section>

      {/* Speed-to-Therapy */}
      <Section icon={Gauge} title="Speed-to-Therapy">
        <div style={statRow}>
          <Stat k="Median time-to-fill" v={fmtDays(tat.median_days)} tone={TEAL} d="referral → first fill" />
          <Stat k="p90 time-to-fill" v={fmtDays(tat.p90_days)} tone={GOLD} d="slowest 10%" />
          <Stat k="Same-week fills" v={`${tat.same_week_pct}%`} d="≤ 7 days" />
          <Stat k="Completed" v={String(tat.completed_count)} d="in window" />
        </div>
        {tat.completed_count > 0 && (
          <BarChart data={tat.distribution.map(b => ({ label: b.label, value: b.count }))} color={BLUE} />
        )}
      </Section>

      {/* Prior-Auth */}
      <Section icon={ShieldCheck} title="Prior-Auth & Payer Access">
        <div className="ins-2col">
          <div>
            <div style={statRow}>
              <Stat k="Approval rate" v={`${pa.approval_pct}%`} tone={TEAL} d="of decisioned PAs" />
              <Stat k="Denial rate" v={`${pa.denial_pct}%`} tone={RED} d="of decisioned PAs" />
            </div>
          </div>
          <DonutChart
            size={150}
            centerLabel={`${pa.approved + pa.denied + pa.pending}`}
            data={[
              { label: 'Approved', value: pa.approved, color: TEAL },
              { label: 'Denied', value: pa.denied, color: RED },
              { label: 'Pending', value: pa.pending, color: GOLD },
            ]}
          />
        </div>
      </Section>

      {/* Adherence */}
      <Section icon={HeartPulse} title="Adherence — supply coverage">
        <div className="ins-2col">
          <div style={statRow}>
            <Stat k="Covered" v={`${adh.coverage_pct}%`} tone={TEAL} d="in-supply now" />
            <Stat k="Overdue" v={String(adh.overdue)} tone={RED} d="gap risk" />
            <Stat k="Due within 7 days" v={String(adh.due_soon)} tone={GOLD} d="refill soon" />
          </div>
          <DonutChart
            size={150}
            centerLabel={`${adh.coverage_pct}%`}
            data={[
              { label: 'Covered', value: adh.covered, color: TEAL },
              { label: 'Overdue', value: adh.overdue, color: RED },
            ]}
          />
        </div>
        <p style={noteStyle}>Coverage proxy from the latest non-reversed fill per Rx — a signal, not a full PDC.</p>
      </Section>

      {/* Exceptions */}
      <Section icon={AlertTriangle} title="Exceptions & Holds">
        <div style={statRow}>
          <Stat k="Open holds" v={String(exc.open_total)} tone={GOLD} d="all reasons" />
          <Stat k="Aging > 3 days" v={String(exc.aging_over_3d)} tone={RED} d="need attention" />
        </div>
        {exc.open_total > 0 && (
          <BarChart data={exc.by_reason.map(r => ({ label: r.reason, value: r.count }))} color={GOLD} />
        )}
      </Section>

      {/* Staff productivity */}
      <Section icon={Users} title="Staff Productivity">
        <div style={statRow}>
          <Stat k="Team members" v={String(staff.team_members)} d="worked prescriptions" />
          <Stat k="Rx handled" v={staff.rx_touches.toLocaleString('en-US')} d="total touches" />
        </div>
        {staff.per_user.length > 0 && (
          <BarChart data={staff.per_user.map(u => ({ label: u.actor, value: u.actions, sublabel: u.role ?? undefined }))} color={BLUE} />
        )}
        {staff.queue_depth.length > 0 && (
          <>
          {queueFx.chips}
          <table {...queueFx.tableProps} style={tableStyle}>
            <thead><tr><th style={thStyle}>Queue (state) {queueFx.funnel('state')}</th><th style={{ ...thStyle, textAlign: 'right' }}>Waiting {queueFx.funnel('count')}</th></tr></thead>
            <tbody>
              {queueFx.rows.map(q => (
                <tr key={q.state}>
                  <td style={tdStyle}>{q.state}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{q.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </Section>
    </div>
  )
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: ReactNode }) {
  return (
    <section style={sectionStyle}>
      <h2 style={cardTitleStyle}><Icon size={16} style={{ marginRight: 8, verticalAlign: '-2px', color: MUTE }} />{title}</h2>
      {children}
    </section>
  )
}
function Stat({ k, v, d, tone }: { k: string; v: string; d?: string; tone?: string }) {
  return (
    <div style={statCard}>
      <div style={statK}>{k}</div>
      <div style={{ ...statV, color: tone ?? 'var(--color-text, #1b2838)' }}>{v}</div>
      {d && <div style={statD}>{d}</div>}
    </div>
  )
}
const fmtDays = (d: number) => (d ? `${d} d` : '—')

const pageStyle: CSSProperties = { padding: 24, maxWidth: '100%', margin: '0 auto' }
const titleStyle: CSSProperties = { fontSize: 22, letterSpacing: '-0.02em', margin: 0 }
const subtitleStyle: CSSProperties = { color: 'var(--color-text-mute, #69788c)', fontSize: 14, margin: '4px 0 0' }
const selectorRow: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, alignItems: 'center' }
const periodBtn: CSSProperties = { fontSize: 12.5, padding: '6px 12px', borderRadius: 999, border: '1px solid var(--color-border, #d2d9e4)', background: 'var(--color-surface, #fff)', color: 'var(--color-text, #1b2838)', cursor: 'pointer' }
const periodBtnActive: CSSProperties = { background: '#0f9488', color: '#fff', borderColor: '#0f9488' }
const sectionStyle: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e4e8ef)', borderRadius: 14, padding: 16, marginBottom: 16 }
const cardTitleStyle: CSSProperties = { fontSize: 14, fontWeight: 650, margin: '0 0 12px' }
const statRow: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 12 }
const statCard: CSSProperties = { border: '1px solid var(--color-border, #e4e8ef)', borderRadius: 11, padding: '10px 12px', background: 'var(--color-surface-2, #fcfdfe)' }
const statK: CSSProperties = { fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--color-text-mute, #69788c)' }
const statV: CSSProperties = { fontSize: 24, fontWeight: 700, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', marginTop: 2 }
const statD: CSSProperties = { fontSize: 11.5, color: 'var(--color-text-mute, #69788c)', marginTop: 1 }
const noteStyle: CSSProperties = { fontSize: 12, color: 'var(--color-text-mute, #69788c)', fontStyle: 'italic', marginTop: 8 }
const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 12 }
const thStyle: CSSProperties = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-mute, #69788c)', borderBottom: '1px solid var(--color-border, #d2d9e4)', padding: '6px 8px' }
const tdStyle: CSSProperties = { padding: '7px 8px', borderBottom: '1px solid var(--color-border, #eef1f5)' }
